import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { CREATE_CITY, ATTRIBUTES_ALL } from "../../utils/constants";
import useRequireAuth from "../../utils/useRequireAuth";
import { useNavigate, Link } from "react-router-dom";
import LayoutHOC from "../LayoutHOC";

const CreateCity = () => {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(ATTRIBUTES_ALL, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });
        const data = await response.json();
        if (data?.data?.state) {
          setStates(data.data.state);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      } finally {
        setLoading(false);
      }
    };
    if (user?.token) fetchStates();
  }, [user?.token]);

  const initialValues = {
    name: "",
    name_hindi: "",
    name_marathi: "",
    name_punjabi: "",
    state: "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("English name is required"),
    state: Yup.string().required("State is required"),
  });

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const Authtoken = user.token;
      const formDataWithFile = new FormData();

      formDataWithFile.append("state_id", values.state);
      formDataWithFile.append("name", values.name);
      formDataWithFile.append("name_hindi", values.name_hindi);
      formDataWithFile.append("name_marathi", values.name_marathi);
      formDataWithFile.append("name_punjabi", values.name_punjabi);

      const response = await fetch(CREATE_CITY, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Authtoken}`,
        },
        body: formDataWithFile,
      });

      if (response.ok) {
        navigate(`/city/`);
      }
      setSubmitting(false);
    } catch (error) {
      setFieldError("form", "An error occurred while submitting the form.");
      setSubmitting(false);
    }
  };

  return (
    <main id="maincontent">
      <div className="p-6 mt-14 min-h-screen bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <Link to="/city" className="p-2 hover:bg-white rounded-lg transition-colors">
              <i className="bi bi-arrow-left text-gray-600 text-xl"></i>
            </Link>
            <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl shadow-lg">
              <i className="bi bi-plus-lg text-white text-2xl"></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Add New City
              </h1>
              <p className="text-gray-500">Create a new city with multilingual names</p>
            </div>
          </div>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-cyan-50">
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <i className="bi bi-geo-alt text-teal-500"></i>
                    Select State
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose the state this city belongs to
                  </p>
                </div>

                <div className="p-6">
                  <Field name="state">
                    {({ field }) => (
                      <div>
                        <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                          <i className="bi bi-map text-teal-500"></i>
                          State
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          {...field}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-gray-700 bg-white"
                          disabled={loading}
                        >
                          <option value="">
                            {loading ? "Loading states..." : "Select a state"}
                          </option>
                          {states.map((state) => (
                            <option key={state.id} value={state.id}>
                              {state.name}
                            </option>
                          ))}
                        </select>
                        <ErrorMessage name="state" component="div" className="mt-1 text-red-500 text-sm" />
                      </div>
                    )}
                  </Field>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <i className="bi bi-translate text-teal-500"></i>
                    City Names in Multiple Languages
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Enter the city name in all supported languages. English is required.
                  </p>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field name="name">
                      {({ field }) => (
                        <div>
                          <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                            <span className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center text-xs font-bold text-blue-600">EN</span>
                            English Name
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...field}
                            type="text"
                            placeholder="Enter city name in English"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-gray-700"
                          />
                          <ErrorMessage name="name" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                      )}
                    </Field>

                    <Field name="name_hindi">
                      {({ field }) => (
                        <div>
                          <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                            <span className="w-6 h-6 bg-orange-100 rounded flex items-center justify-center text-xs font-bold text-orange-600">HI</span>
                            Hindi Name
                          </label>
                          <input
                            {...field}
                            type="text"
                            placeholder="हिंदी में शहर का नाम दर्ज करें"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-gray-700"
                          />
                          <ErrorMessage name="name_hindi" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                      )}
                    </Field>

                    <Field name="name_marathi">
                      {({ field }) => (
                        <div>
                          <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                            <span className="w-6 h-6 bg-green-100 rounded flex items-center justify-center text-xs font-bold text-green-600">MR</span>
                            Marathi Name
                          </label>
                          <input
                            {...field}
                            type="text"
                            placeholder="मराठीत शहराचे नाव प्रविष्ट करा"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-gray-700"
                          />
                          <ErrorMessage name="name_marathi" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                      )}
                    </Field>

                    <Field name="name_punjabi">
                      {({ field }) => (
                        <div>
                          <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                            <span className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center text-xs font-bold text-purple-600">PA</span>
                            Punjabi Name
                          </label>
                          <input
                            {...field}
                            type="text"
                            placeholder="ਪੰਜਾਬੀ ਵਿੱਚ ਸ਼ਹਿਰ ਦਾ ਨਾਮ ਦਰਜ ਕਰੋ"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-gray-700"
                          />
                          <ErrorMessage name="name_punjabi" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                      )}
                    </Field>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                  <Link
                    to="/city"
                    className="px-6 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors font-medium"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl hover:from-teal-600 hover:to-cyan-600 disabled:from-gray-300 disabled:to-gray-300 transition-all font-medium shadow-md flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="bi bi-arrow-repeat animate-spin"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg"></i>
                        Save City
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </main>
  );
};

export default LayoutHOC(CreateCity);
